/**
 * Chat Infrastructure - API Adapter
 *
 * Wraps Orval-generated hooks with type-safe BaseResponseDto handling.
 * Eliminates need for type casting in application/presentation layers.
 */

import type { ChatRoomResponseDto, BaseResponseDto } from "@semo/api-contracts";
import { useChatControllerGetRoom } from "@semo/api-contracts";

/**
 * Unwrap BaseResponseDto to get actual data
 * Safely extracts data from Orval's BaseResponseDto wrapper
 */
function unwrapResponse<T>(
  response: BaseResponseDto | undefined,
): T | undefined {
  if (!response) return undefined;
  // BaseResponseDto has a 'data' field containing the actual response
  // Type guard: check if response has data property
  if ("data" in response && response.data !== undefined) {
    return response.data as T;
  }
  return undefined;
}

/**
 * Type-safe wrapper for useChatControllerGetRoom
 *
 * Returns unwrapped ChatRoomResponseDto instead of BaseResponseDto
 */
export function useChatRoom(chatRoomId: string) {
  const query = useChatControllerGetRoom(chatRoomId);

  return {
    ...query,
    room: unwrapResponse<ChatRoomResponseDto>(query.data),
  } as Omit<typeof query, "data"> & {
    room: ChatRoomResponseDto | undefined;
  };
}

/**
 * Type-safe wrapper for useChatControllerGetRoom (aliased for join context)
 *
 * Returns unwrapped join data with isAnonymous flag
 */
export function useJoinChatRoom(chatRoomId: string) {
  const query = useChatControllerGetRoom(chatRoomId);

  interface JoinData {
    isAnonymous?: boolean;
    roomId?: string;
  }

  return {
    ...query,
    joinData: unwrapResponse<JoinData>(query.data),
  } as Omit<typeof query, "data"> & {
    joinData: JoinData | undefined;
  };
}

/**
 * Export raw Orval hooks for special cases
 * (Prefer using wrapper functions above)
 */
export { useChatControllerGetRoom as useChatControllerGetRoomRaw };
